import type { Character, CharacterId } from "@schema/catalog.ts";
import { useId, useMemo, useState } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { useFilteredRoad } from "@/lib/filters/context.tsx";
import { setFilters, useFilters } from "@/state/filters.ts";
import styles from "./controls.module.css";

/**
 * The one people tell their friends about: pick Doom, or Reed, or Yelena, and
 * 81 stops collapse to the handful that set them up.
 *
 * Design rules that make it feel obvious:
 *  - **Roster first.** Characters actually announced for Doomsday lead, in
 *    order of how much of the road belongs to them; everyone else is one
 *    disclosure away.
 *  - **Live counts on every chip**, computed with every other filter applied
 *    but the character facet ignored — the number on the chip is the number of
 *    stops you'll be looking at after you click it.
 *  - **No dead chips.** A character with nothing curated yet isn't offered.
 *  - **Selection never hides.** Search and the collapse cap both keep selected
 *    chips on screen, so you can always undo what you just did.
 *
 * Selecting several is an OR: "everything that sets up any of these people".
 */

const CONFIDENCE_RANK = { confirmed: 0, reported: 1, speculative: 2 } as const;

const CONFIDENCE_NOTE = {
  confirmed: "",
  reported: " (reported casting)",
  speculative: " (rumoured casting)",
} as const;

/** How many roster chips before the list folds. */
const COLLAPSED = 10;

interface Chip {
  character: Character;
  count: number;
  selected: boolean;
}

const matchesQuery = (character: Character, query: string): boolean => {
  if (query === "") return true;
  const needle = query.toLowerCase();
  return (
    character.name.toLowerCase().includes(needle) ||
    character.aliases.some((alias) => alias.toLowerCase().includes(needle))
  );
};

export function CharacterFilter() {
  const catalog = useCatalog();
  const { vocabulary } = catalog.project;
  const filters = useFilters();
  const { characterCounts } = useFilteredRoad();
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const searchId = useId();

  const selected = useMemo(() => new Set(filters.characters), [filters.characters]);

  /*
   * Example names for the search box, taken from this project's own roster.
   *
   * It read "Doom, Reed, Yelena…" for every project, which is the sort of
   * thing that only looks wrong once a second guide exists — a PAW Patrol
   * road inviting you to search for Doctor Doom. Roster members first,
   * because those are the faces the road is built around and the ones someone
   * is most likely to type.
   */
  const searchHint = useMemo(() => {
    const names = [...catalog.characters]
      .sort((a, b) => Number(b.targetRoster) - Number(a.targetRoster))
      .slice(0, 3)
      .map((character) => character.name.split(" ")[0]);
    return names.length > 0 ? `${names.join(", ")}…` : "Search characters";
  }, [catalog.characters]);

  const { roster, others } = useMemo(() => {
    const chips: Chip[] = [];
    for (const character of catalog.characters) {
      const count = characterCounts.get(character.id) ?? 0;
      const isSelected = selected.has(character.id);
      // A chip that can only ever return nothing is worse than no chip at all.
      if (count === 0 && !isSelected) continue;
      chips.push({ character, count, selected: isSelected });
    }
    const order = (a: Chip, b: Chip) =>
      CONFIDENCE_RANK[a.character.rosterConfidence] -
        CONFIDENCE_RANK[b.character.rosterConfidence] ||
      b.count - a.count ||
      a.character.name.localeCompare(b.character.name);
    return {
      roster: chips.filter((c) => c.character.targetRoster).sort(order),
      others: chips.filter((c) => !c.character.targetRoster).sort(order),
    };
  }, [catalog.characters, characterCounts, selected]);

  const toggle = (id: CharacterId) =>
    setFilters((prev) => ({
      ...prev,
      characters: prev.characters.includes(id)
        ? prev.characters.filter((other) => other !== id)
        : [...prev.characters, id],
    }));

  const searching = query.trim() !== "";
  const visibleRoster = roster.filter(
    (chip) => chip.selected || matchesQuery(chip.character, query),
  );
  const visibleOthers = others.filter(
    (chip) => chip.selected || matchesQuery(chip.character, query),
  );
  // Searching or expanding both mean "show me everything that matches".
  // Collapsed, the list is capped — but a chip you've selected is always in it,
  // wherever it ranks, because a filter you can't see is a filter you can't undo.
  const openList = searching || expanded;
  const shownRoster = openList
    ? visibleRoster
    : visibleRoster.filter((chip, index) => index < COLLAPSED || chip.selected);
  const hiddenRoster = visibleRoster.length - shownRoster.length;
  const shownOthers = openList ? visibleOthers : visibleOthers.filter((chip) => chip.selected);

  const renderChip = ({ character, count, selected: on }: Chip) => (
    <li key={character.id}>
      <button
        type="button"
        className={`${styles.chip} ${on ? styles.chipOn : ""}`}
        aria-pressed={on}
        onClick={() => toggle(character.id)}
        title={`${character.name}${CONFIDENCE_NOTE[character.rosterConfidence]}: ${count} ${
          count === 1 ? vocabulary.stop : vocabulary.stops
        }`}
      >
        {character.name}
        <span className={styles.chipCount}>{count}</span>
      </button>
    </li>
  );

  return (
    <div className={styles.field}>
      <div className={styles.head}>
        <span className={styles.label} id={`${searchId}-label`}>
          Characters
        </span>
        {filters.characters.length > 0 && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => setFilters((prev) => ({ ...prev, characters: [] }))}
          >
            {`Clear ${filters.characters.length}`}
          </button>
        )}
      </div>

      <input
        id={searchId}
        type="search"
        className={styles.search}
        value={query}
        placeholder={searchHint}
        aria-label="Search characters"
        onChange={(event) => setQuery(event.target.value)}
      />

      <p className={styles.groupLabel}>
        On the {catalog.target.shortTitle} {vocabulary.roster}
      </p>
      {shownRoster.length > 0 ? (
        <ul className={styles.chips} aria-labelledby={`${searchId}-label`}>
          {shownRoster.map(renderChip)}
        </ul>
      ) : (
        <p className={styles.hint}>No roster match.</p>
      )}

      {shownOthers.length > 0 && (
        <>
          <p className={styles.groupLabel}>Everyone else</p>
          <ul className={styles.chips}>{shownOthers.map(renderChip)}</ul>
        </>
      )}

      {!searching && (expanded || hiddenRoster + others.length - shownOthers.length > 0) && (
        <button
          type="button"
          className={styles.disclosure}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded
            ? "Show fewer"
            : `Show ${hiddenRoster + others.length - shownOthers.length} more`}
        </button>
      )}

      <p className={styles.hint}>
        Pick a face and the {vocabulary.road} collapses to the {vocabulary.stops} that set them up.
        Pick several for anyone of them.
      </p>
    </div>
  );
}
